/**
 * Shows the published release notes in the Produktnyheter view of the round "?" bubble
 * at the bottom left — Pendo's Resource Center, not Elevio.
 *
 * Pendo's own announcements are authored in their dashboard and cannot be repointed
 * from here, but the Resource Center renders into our DOM: their `<ol>` is the panel's
 * scroll container, so the releases go in as its first row and the two read as one
 * list. Everything here is keyed off structure rather than their hashed class names,
 * and if it stops matching nothing is inserted and their list shows as it does today.
 */
import config from 'App/babel/config';
import T, { getCurrentLanguage } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';

const CONTAINER_ID = 'pendo-resource-center-container';
const FRAME_ID = 'up-product-news-frame';
const HEIGHT_MESSAGE = 'upsales-product-news-height';
const MAX_HEIGHT = 4000;
/** After the page has loaded, how long its height may still be on the way. */
const SETTLE_MS = 2000;
/** How long a page that never loads at all is given. */
const LOAD_MS = 20000;

let pageOrigin: string | null = null;
let observer: MutationObserver | null = null;
let frame: HTMLIFrameElement | null = null;
let row: HTMLLIElement | null = null;
let sized = false;
let unavailable = false;
let driftLogged = false;

const originOf = (url: string) => {
	try {
		return new URL(url, window.location.href).origin;
	} catch (e) {
		logError(e, 'Product news: PRODUCT_NEWS_URL is not a valid url', config.PRODUCT_NEWS_URL);
		return null;
	}
};

/**
 * `compact=1` asks the page for its digest — a headline and intro per release, each
 * linking to the full page — rather than the reader, which does not fit a panel this
 * narrow. The language travels in the url because the page only appends `?lang=` for
 * its own modules.
 */
const frameUrl = () => {
	const lang = getCurrentLanguage()?.toLowerCase().startsWith('sv') ? 'sv' : 'en';
	const separator = config.PRODUCT_NEWS_URL.includes('?') ? '&' : '?';
	return `${config.PRODUCT_NEWS_URL}${separator}compact=1&lang=${lang}`;
};

/** Nothing to show: leave the panel as Pendo's own list and stop looking. */
const giveUp = (reason: string) => {
	unavailable = true;
	observer?.disconnect();
	observer = null;
	window.removeEventListener('message', onHeight);
	row?.remove();
	row = null;
	frame = null;
	logError(new Error(`Product news is unavailable: ${reason}`), 'Product news', config.PRODUCT_NEWS_URL);
};

function onHeight(event: MessageEvent) {
	if (event.origin !== pageOrigin || event.source !== frame?.contentWindow) {
		return;
	}
	const data = event.data as { type?: string; height?: unknown };
	const height = Number(data?.height);
	if (data?.type !== HEIGHT_MESSAGE || !(height > 0)) {
		return;
	}
	frame.style.height = `${Math.min(Math.round(height), MAX_HEIGHT)}px`;
	sized = true;
}

/**
 * Their markup moved: a sub-view listing rows, none of which is a guide. Logged once,
 * because otherwise this feature dies silently and nobody finds out.
 */
const reportDrift = (container: Element) => {
	if (driftLogged || !container.querySelector('ol li') || container.querySelector('ol li [id^="pendo-g-"]')) {
		return;
	}
	driftLogged = true;
	logError(new Error('Product news: the Pendo announcements list no longer matches'), 'Product news');
};

const insert = () => {
	const container = document.getElementById(CONTAINER_ID);
	if (unavailable || !container || document.getElementById(FRAME_ID)) {
		return;
	}
	const list = container.querySelector('ol li [id^="pendo-g-"]')?.closest('ol');
	if (!list) {
		reportDrift(container);
		return;
	}

	sized = false;
	frame = document.createElement('iframe');
	frame.id = FRAME_ID;
	frame.title = T('productNews.title');
	frame.src = frameUrl();
	// Sized to its content, with the list doing the scrolling — so it must not scroll
	// itself, or the reader gets a scrollbar inside a scrollbar.
	frame.setAttribute('scrolling', 'no');
	frame.setAttribute('referrerpolicy', 'no-referrer');
	// The pages host also serves customer-authored html, so deny it everything it does
	// not need — top-level navigation above all. `allow-same-origin` keeps the page's
	// OWN origin (it is not ours), without which the height below arrives as "null"
	// and is rejected.
	frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
	frame.style.cssText = 'width:100%;height:120px;border:0;display:block;background:#fff';

	// Pendo's rows carry their own gutter, so copy it rather than restate it; the page
	// inside renders edge to edge and lines up with the rows below.
	row = document.createElement('li');
	const sibling = list.querySelector('li');
	if (sibling) {
		const { paddingLeft, paddingRight } = window.getComputedStyle(sibling);
		row.style.padding = `0 ${paddingRight} 0 ${paddingLeft}`;
	}
	row.style.listStyle = 'none';
	row.appendChild(frame);
	list.prepend(row);

	// A page that loads and still reports no height has nothing to show. The checks are
	// tied to the load, so a slow page is waited for rather than written off, and both
	// ignore a frame Pendo has already replaced — that frame's failure is not this one's.
	const current = frame;
	current.addEventListener('load', () => {
		window.setTimeout(() => {
			if (current.isConnected && !sized) {
				giveUp('the page reported no height');
			}
		}, SETTLE_MS);
	});
	window.setTimeout(() => {
		if (current.isConnected && !sized) {
			giveUp('the page did not load');
		}
	}, LOAD_MS);
};

/** Idempotent: safe to call more than once. No-op until a page is configured. */
export const installProductNews = () => {
	if (observer || unavailable || !config.PRODUCT_NEWS_URL) {
		return;
	}
	pageOrigin = originOf(config.PRODUCT_NEWS_URL);
	if (!pageOrigin) {
		return;
	}
	window.addEventListener('message', onHeight);
	// The panel is built and torn down on every open, so the whole body is watched.
	// Coalesced to a frame: Pendo's open animation produces mutations in bursts.
	let queued = false;
	observer = new MutationObserver(() => {
		if (queued) {
			return;
		}
		queued = true;
		window.requestAnimationFrame(() => {
			queued = false;
			insert();
		});
	});
	observer.observe(document.body, { childList: true, subtree: true });
	insert();
};
